import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import {
  Building2,
  Check,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Gauge,
  Loader2,
  LogOut,
  Receipt,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { cn } from '@/utils';
import SiteLogo from '@/components/common/SiteLogo';
import ThemeToggle from '@/components/common/ThemeToggle';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountAuthApi } from '@/services/endpoints/account/auth';
import type { AccountMembership } from '@/services/api/accountTypes';
import AccountInstrument from './AccountInstrument';
import AvatarInitials from "@/components/ui/avatar-initials";

/**
 * Console shell for the organization account.
 *
 * ── Direction contract (surface: /account, mode: Operate) ──
 * THESIS: the account's state is a constant readout, not a page you navigate
 *   to. Refuses the console default where status hides inside an "Overview"
 *   tab and every other section forgets the account exists.
 * OWN-WORLD: the incumbent ai2aim system, unchanged — navy/cyan on the
 *   --landing/semantic tokens, and Manrope throughout — money, dates and ids
 *   included, since the site consolidated to a single family on 2026-09-18.
 *   They keep tabular figures so columns still align. Hairline-bordered
 *   panels, definition rows, no card grids.
 * STORY: an owner arrives asking "what am I paying, is it healthy, change one
 *   thing" and can answer the first two before reading a single section.
 * FIRST VIEWPORT: condensed header, then the instrument strip — status, plan,
 *   seats, next charge — then the section rail and the work pane. Trouble
 *   (past due, suspended) takes over the strip with the recovery action inline.
 * FORM: persistent instrument + swapping work panes (candidate 4 of the
 *   ordered structures; surface seed 1a166d67).
 *
 * Deliberately not SiteLayout: this is a place of work, not a page of the
 * marketing site, so it drops the marketing nav and footer and keeps a single
 * quiet route back to ai2aim.ai.
 */

interface AccountNavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  /** Owner-only server-side; hidden rather than shown as a dead end. */
  ownerOnly?: boolean;
}

export const ACCOUNT_NAV: AccountNavItem[] = [
  { to: '/account', label: 'Overview', icon: Gauge, end: true },
  { to: '/account/subscription', label: 'Subscription', icon: Receipt, ownerOnly: true },
  { to: '/account/billing', label: 'Billing', icon: CreditCard, ownerOnly: true },
  { to: '/account/organization', label: 'Organization', icon: Building2 },
  { to: '/account/profile', label: 'Profile', icon: UserRound },
  { to: '/account/security', label: 'Security', icon: ShieldCheck },
];

export default function AccountShell() {
  const { context, isOwner, displayName, signOut, switchOrganization } =
    useAccountAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);

  // Only fetched to decide whether a switcher is warranted at all; a
  // single-org owner never sees one.
  const { data: organizations } = useQuery({
    queryKey: ['account', 'memberships'],
    queryFn: accountAuthApi.getOrganizations,
    staleTime: 10 * 60_000,
  });
  const memberships = organizations?.organizations ?? [];

  const handleSwitch = async (membership: AccountMembership) => {
    if (membership.id === context?.organization?.id) return;
    setSwitchingTo(membership.id);
    try {
      await switchOrganization(membership);
      // Every cached read was scoped to the previous organization.
      await queryClient.invalidateQueries();
      toast.success(`Switched to ${membership.name}.`);
    } catch {
      toast.error('Could not switch organization.');
    } finally {
      setSwitchingTo(null);
    }
  };

  const items = ACCOUNT_NAV.filter((item) => !item.ownerOnly || isOwner);
  const current =
    items.find((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to))) ??
    items[0];

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/account/sign-in', { replace: true });
  };

  const railLink = ({ isActive }: { isActive: boolean }) =>
    cn(
      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
      isActive
        ? 'bg-brand-cyan/10 font-semibold text-foreground'
        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
    );

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label="AI2Aim home">
            <SiteLogo className="h-7 w-7" />
            <span className="text-sm font-semibold tracking-tight">Account</span>
          </Link>

          {context?.organization?.name && (
            <>
              <span className="hidden h-4 w-px bg-border sm:block" aria-hidden="true" />
              {memberships.length > 1 ? (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="hidden max-w-[14rem] items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground sm:inline-flex"
                    >
                      <span className="truncate">{context.organization.name}</span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="start"
                      sideOffset={8}
                      className="z-50 min-w-[16rem] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg"
                    >
                      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Your organizations
                      </p>
                      {memberships.map((membership) => (
                        <DropdownMenu.Item
                          key={membership.id}
                          disabled={switchingTo !== null}
                          onSelect={() => void handleSwitch(membership)}
                          className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-muted"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">{membership.name}</span>
                            <span className="block truncate text-xs capitalize text-muted-foreground">
                              {membership.role}
                            </span>
                          </span>
                          {membership.id === context.organization.id ? (
                            <Check className="h-4 w-4 shrink-0 text-brand-cyan" aria-hidden="true" />
                          ) : switchingTo === membership.id ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                          ) : null}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              ) : (
                <span className="hidden truncate text-sm text-muted-foreground sm:block">
                  {context.organization.name}
                </span>
              )}
            </>
          )}

          <div className="ml-auto flex items-center gap-1">
            <a
              href="/"
              className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground sm:inline-flex"
            >
              ai2aim.ai
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <ThemeToggle />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/60"
                >
                  {/* The circle is the shared component now, which the profile
                      page also uses — it derives its own initials from the
                      name, so the context's `initials` is no longer read here. */}
                  <AvatarInitials name={displayName} size="sm" />
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 min-w-[15rem] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg"
                >
                  <div className="px-3 py-2">
                    <p className="truncate text-sm font-semibold">{displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {context?.user?.email ?? context?.employee?.workEmail}
                    </p>
                    {context?.role?.name && (
                      <p className="mt-1 text-xs capitalize text-muted-foreground">
                        {context.role.name}
                      </p>
                    )}
                  </div>
                  <DropdownMenu.Separator className="my-1 h-px bg-border" />
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/account/profile"
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-muted"
                    >
                      <UserRound className="h-4 w-4" aria-hidden="true" />
                      Profile
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => void handleSignOut()}
                    disabled={signingOut}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive outline-none data-[highlighted]:bg-destructive/10"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    {signingOut ? 'Signing out…' : 'Sign out'}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
      </header>

      <AccountInstrument />

      {/* Mobile section picker — six sections do not fit a tab strip honestly. */}
      <div className="border-b border-border px-4 py-2.5 lg:hidden">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-medium"
            >
              <span className="flex items-center gap-2.5">
                {current && <current.icon className="h-4 w-4 text-brand-cyan" aria-hidden="true" />}
                {current?.label}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              className="z-50 w-[calc(100vw-2rem)] rounded-xl border border-border bg-popover p-1.5 shadow-lg"
            >
              {items.map((item) => (
                <DropdownMenu.Item key={item.to} asChild>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-muted"
                  >
                    <item.icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 gap-8 px-4 py-8 sm:px-6">
        <nav aria-label="Account sections" className="hidden w-52 shrink-0 lg:block">
          <div className="sticky top-[4.5rem] space-y-0.5">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={railLink}>
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'absolute left-0 h-5 w-[2px] rounded-full bg-brand-cyan transition-opacity',
                        isActive ? 'opacity-100' : 'opacity-0'
                      )}
                      aria-hidden="true"
                    />
                    <item.icon
                      className={cn('h-4 w-4', isActive ? 'text-brand-cyan' : 'text-muted-foreground')}
                      aria-hidden="true"
                    />
                    {item.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <motion.main
          key={pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          className="min-w-0 flex-1"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
